<script setup>
import { computed, onMounted, ref } from 'vue';
import UserBar from '@/js/core/components/parts/user-bar/UserBar.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import ProfileHeader from '@/js/core/components/pages/profile/ProfileHeader.vue';
import ProfileMetaSection from '@/js/core/components/pages/profile/ProfileMetaSection.vue';
import ProfileBioSection from '@/js/core/components/pages/profile/ProfileBioSection.vue';
import ProfileSettingsSection from '@/js/core/components/pages/profile/ProfileSettingsSection.vue';
import { useProfileStore } from '@/js/core/stores/profile-store';
import { audienceGroupLabel } from '@/js/core/utils/audience-group';
import { toPersonShape } from '@/js/core/utils/profile-shape';

const profileStore = useProfileStore();
const hasRequestedProfile = ref(false);

const profile = computed(() => profileStore.profile);
const hasProfile = computed(() => Boolean(profile.value));
const isLoadingProfile = computed(() => {
  return !hasProfile.value && (!hasRequestedProfile.value || profileStore.isLoading);
});
const profileStateMessage = computed(() => {
  if (hasProfile.value || isLoadingProfile.value) return '';

  return profileStore.error || 'We could not load your profile. Please try again later.';
});

const identity = computed(() => profile.value?.identity || {});
const identityName = computed(() => {
  const name = identity.value.name || {};

  return name.full || name.display || 'GSD Now user';
});
const audienceGroup = computed(() => audienceGroupLabel(profile.value));
const person = computed(() => toPersonShape(profile.value ?? {}));

const bio = computed(() => profile.value?.bio || '');
const visibility = computed(() => profile.value?.visibility || {});
const timezone = computed(() => profile.value?.timezone || {});

onMounted(async () => {
  hasRequestedProfile.value = true;
  await profileStore.fetchCurrentProfile();
});
</script>

<template>
  <UserBar />
  <NavigationPanel />
  <main id="main-content" class="profile-page_main-content">
    <section id="page-content" class="profile-page" :aria-busy="isLoadingProfile">
      <ProfileHeader
        :is-loading="isLoadingProfile"
        :state-message="profileStateMessage"
        :audience-group="audienceGroup"
        :name="identityName"
      />

      <template v-if="hasProfile">
        <ProfileMetaSection :person="person" />

        <ProfileBioSection :bio="bio" />

        <ProfileSettingsSection :visibility="visibility" :timezone="timezone" />
      </template>
    </section>
  </main>
</template>
